import logo from "assets/svg/fullLogo.svg";
import { LogoutIcon, UserCircleIcon } from "assets/icons";
import { getUserDetails, isTokenValid } from "utils/localStorageServices";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useLogout } from "hooks/useLogout";
import NewLogin from "views/Authentication/Login/new-login-flow";
import FirstOnboardingSessionPage from "components/onboardingSession/FirstOnboardingSessionPage";
import ProfilePicture from "components/UI/Avatar/ProfilePicture";
import { getAppUrl } from "utils/subdomainDetection";

// Header Component
export default function Header({
  callbackForLogout,
  setOpenInviteDrawer,
  openInviteDrawer,
  shouldOpenLoginModal,
  setShouldOpenLoginModal,
  shouldShowLogo = true,
  className = "pt-[24.5px]",
}: {
  callbackForLogout?: () => void;
  setOpenInviteDrawer?: Dispatch<SetStateAction<boolean>>;
  openInviteDrawer?: boolean;
  shouldOpenLoginModal?: boolean;
  setShouldOpenLoginModal?: Dispatch<SetStateAction<boolean>>;
  shouldShowLogo?: boolean;
  className?: string;
}) {
  const [fullName, setFullName] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [openLoginModal, setOpenLoginModal] = useState(false);
  const [openOnboardingModal, setOpenOnboardingModal] = useState(false);

  const appOverviewUrl = getAppUrl("/overview");

  // Check if user is logged in
  const userDetails = getUserDetails();
  const hasValidAuth = !!userDetails && isTokenValid();

  // Effect to set user details
  useEffect(() => {
    if (!userDetails) return;

    if (userDetails) {
      userDetails.fullName && setFullName(userDetails.fullName as string);
      setIsLoggedIn(true);
    }
  }, [userDetails]);

  const logout = useLogout(false);
  const logoutFunc = async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setFullName("");
      setIsLoggedIn(false);
      setIsLoggingOut(false);
      callbackForLogout?.();

      if (window.location.hostname.endsWith(".stage.cv")) {
        const returnTo = window.location.href;
        const appLogoutUrl = getAppUrl(
          `/auth/logout?returnTo=${encodeURIComponent(returnTo)}`,
        );
        window.location.replace(appLogoutUrl);
      }
    }
  };

  useEffect(() => {
    if (shouldOpenLoginModal) {
      setOpenLoginModal(true);
    }
  }, [shouldOpenLoginModal]);

  return (
    <header className={className}>
      <nav className="flex h-10 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {shouldShowLogo && (
            <img src={logo} alt="logo" className="w-[120px] shrink-0" />
          )}
        </div>

        {isLoggedIn ? (
          <div className="flex max-w-[52vw] shrink-0 items-center gap-2 rounded-lg border border-[#EFE9DF] bg-[#F2ECE2] px-2 py-1 hover:bg-[#e0d7ca] sm:max-w-none">
            <a href={appOverviewUrl} className="flex min-w-0 items-center gap-2">
              <ProfilePicture size={24} enabled={hasValidAuth} />
              {/*  */}
              <p className="truncate text-sm sm:text-base">{fullName}</p>
            </a>
            <button
              type="button"
              aria-label="Log out"
              disabled={isLoggingOut}
              onClick={logoutFunc}
            >
              <LogoutIcon />
            </button>
          </div>
        ) : (
          <button
            className="flex shrink-0 items-center gap-2 rounded-lg border border-[#EFE9DF] bg-[#F2ECE2] px-2 py-1 text-base font-monaMedium text-[#20242A] hover:bg-[#e0d7ca] sm:text-lg"
            // onClick={() => navigate("/login")}
            onClick={() => setOpenLoginModal(true)}
          >
            <UserCircleIcon />
            Login
          </button>
        )}
      </nav>

      <NewLogin
        isModal={true}
        open={openLoginModal}
        handleClose={(shouldOpenOnboardingModal = false) => {
          setOpenLoginModal(false);
          setShouldOpenLoginModal?.(false);
        }}
        handleCallbackForLogout={() => {
          if (shouldOpenLoginModal) {
            setOpenInviteDrawer?.(true);
            setShouldOpenLoginModal?.(false);
          }
        }}
      />

      <FirstOnboardingSessionPage
        isModal={true}
        open={openOnboardingModal}
        handleClose={() => {
          setOpenOnboardingModal(false);
          // refetchGetUserProfilePicture();
        }}
      />
    </header>
  );
}
